/**
 * Test telemetry hook for E2E feedback loops.
 *
 * Collects rendering stats, coordinates, screenshots, and sends to backend.
 */

import { useCallback } from "react";

import {
  getNonDeletedElements,
  isArrowElement,
  isFrameLikeElement,
  isTextElement,
} from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

import { TELEMETRY_CONFIG } from "../config/telemetry";
import { calculateCanvasBounds } from "../utils/canvasUtils";
import { getBackendUrl } from "../utils/backendUrl";
import { errorCollector } from "../utils/errorCollector";
import { blobToDataURL, captureCanvas } from "../utils/screenshotHelper";

import type { CanvasBounds } from "../utils/canvasUtils";

// Type definitions matching backend models
interface ElementCoordinates {
  id: string;
  type: string;
  x: number;
  y: number;
  width: number;
  height: number;
  angle: number;
  text?: string;
  containerId?: string | null;
  boundElements?: Array<{ id: string; type: string }> | null;
}

interface RenderStats {
  total_elements: number;
  text_elements: number;
  arrow_elements: number;
  bound_arrows: number;
  frame_elements: number;
  unbound_text: number;
  canvas_bounds: CanvasBounds;
}

interface TestReport {
  run_id: string;
  timestamp: number;
  input: {
    prompt: string;
    chart_type?: string;
    config?: any;
  };
  output: {
    elements_json: any[];
    elements_count: number;
    elements_coordinates: ElementCoordinates[];
  };
  render_stats: RenderStats;
  runtime_logs: {
    errors: string[];
    warnings: string[];
  };
  screenshot?: {
    data_url: string;
    width: number;
    height: number;
  };
  config: {
    enable_feedback_loop: boolean;
    model_name: string;
  };
  timing?: {
    ttfr_ms: number | null; // Time to first render
    total_ms: number | null; // Total generation time
    start_ts: number; // Start timestamp
    first_partial_ts?: number; // First partial event timestamp
    optimized_ts?: number; // Optimization complete timestamp
  };
  /** Code string received from backend (e.g. result.rawCode). For pipeline traceability. */
  code_received_by_frontend?: string;
  /** Code string of the rendered canvas (excalidraw JSON). For pipeline traceability. */
  code_rendered?: string;
}

/**
 * Extract element coordinates and metadata.
 */
function extractElementCoordinates(
  elements: readonly ExcalidrawElement[],
): ElementCoordinates[] {
  const nonDeleted = getNonDeletedElements(elements);

  return nonDeleted.map((el) => {
    const coords: ElementCoordinates = {
      id: el.id,
      type: el.type,
      x: el.x,
      y: el.y,
      width: el.width,
      height: el.height,
      angle: el.angle,
    };

    // Add text-specific fields
    if (isTextElement(el)) {
      coords.text = el.text;
      coords.containerId = el.containerId;
    }

    // Add bound elements
    if (el.boundElements && el.boundElements.length > 0) {
      coords.boundElements = el.boundElements.map((be) => ({
        id: be.id,
        type: be.type,
      }));
    }

    return coords;
  });
}

/**
 * Collect rendering statistics.
 */
function collectRenderStats(
  elements: readonly ExcalidrawElement[],
): RenderStats {
  const nonDeleted = getNonDeletedElements(elements);
  const textElements = nonDeleted.filter(isTextElement);
  const arrows = nonDeleted.filter(isArrowElement);

  return {
    total_elements: nonDeleted.length,
    text_elements: textElements.length,
    arrow_elements: arrows.length,
    bound_arrows: arrows.filter((el: any) => el.startBinding || el.endBinding)
      .length,
    frame_elements: nonDeleted.filter(isFrameLikeElement).length,
    unbound_text: textElements.filter((el: any) => !el.containerId).length,
    canvas_bounds: calculateCanvasBounds(elements),
  };
}

/**
 * Hook for test telemetry.
 */
export function useTestTelemetry() {
  /**
   * Collect and send test report to backend.
   */
  const collectAndSendReport = useCallback(
    async (
      excalidrawAPI: ExcalidrawImperativeAPI,
      runId: string,
      prompt: string,
      chartType?: string,
      enableFeedbackLoop: boolean = false,
      options?: {
        /** Code received from backend (e.g. result.rawCode from SSE optimized event). */
        codeReceived?: string;
        /** Code of rendered canvas; if omitted, built from current scene elements. */
        codeRendered?: string;
      },
    ): Promise<void> => {
      try {
        console.log(`[Telemetry] Collecting test report for run_id=${runId}`);

        const elements = excalidrawAPI.getSceneElements();

        // IMPORTANT: Allow telemetry even with 0 elements for automated testing
        // This helps diagnose rendering failures in E2E tests
        if (elements.length === 0) {
          console.warn(
            "[Telemetry] ⚠️ No elements on canvas, but proceeding with telemetry for diagnostics",
          );
        }

        // Collect coordinates and stats
        const coordinates = extractElementCoordinates(elements);
        const stats = collectRenderStats(elements);

        // Get runtime logs
        const logs = errorCollector.getAndClear();

        // Capture screenshot
        let screenshot: TestReport["screenshot"] | undefined;
        try {
          const { blob, width, height } = await captureCanvas(excalidrawAPI);
          const dataUrl = await blobToDataURL(blob);
          screenshot = {
            data_url: dataUrl,
            width,
            height,
          };
          console.log(
            `[Telemetry] Screenshot captured: ${width}x${height}, ${Math.round(
              blob.size / 1024,
            )}KB`,
          );
        } catch (err) {
          console.error("[Telemetry] Failed to capture screenshot:", err);
        }

        // Build code_rendered from current scene if not provided (for pipeline traceability)
        const codeRendered =
          options?.codeRendered ??
          (elements.length > 0
            ? JSON.stringify(
                {
                  type: "excalidraw",
                  version: 2,
                  elements: elements.map((el: ExcalidrawElement) => {
                    try {
                      return JSON.parse(JSON.stringify(el));
                    } catch {
                      return {
                        id: el.id,
                        type: el.type,
                        x: el.x,
                        y: el.y,
                        width: el.width,
                        height: el.height,
                      };
                    }
                  }),
                },
                null,
                2,
              )
            : undefined);

        // Build test report
        const report: TestReport = {
          run_id: runId,
          timestamp: Date.now(),
          input: {
            prompt,
            chart_type: chartType,
          },
          output: {
            elements_json: elements.map((el: ExcalidrawElement) => {
              try {
                // Deep clone to ensure serializability and remove circular refs
                return JSON.parse(JSON.stringify(el));
              } catch (err) {
                console.warn(
                  `[Telemetry] Failed to serialize element ${el.id}:`,
                  err,
                );
                // Fallback: return minimal safe representation
                return {
                  id: el.id,
                  type: el.type,
                  x: el.x,
                  y: el.y,
                  width: el.width,
                  height: el.height,
                  error: "serialization_failed",
                };
              }
            }),
            elements_count: elements.length,
            elements_coordinates: coordinates,
          },
          render_stats: stats,
          runtime_logs: logs,
          screenshot,
          config: {
            enable_feedback_loop: enableFeedbackLoop,
            model_name: TELEMETRY_CONFIG.testModelName,
          },
          code_received_by_frontend: options?.codeReceived,
          code_rendered: codeRendered,
        };

        console.log("[Telemetry] Test report summary:", {
          run_id: runId,
          elements: stats.total_elements,
          errors: logs.errors.length,
          warnings: logs.warnings.length,
          has_screenshot: !!screenshot,
        });

        // Send to backend
        await sendTestReport(report);

        console.log(`[Telemetry] ✓ Test report sent successfully`);
      } catch (err) {
        console.error("[Telemetry] Failed to collect/send test report:", err);
        errorCollector.pushError(
          `Telemetry failed: ${
            err instanceof Error ? err.message : String(err)
          }`,
        );
      }
    },
    [],
  );

  return { collectAndSendReport };
}

/**
 * Send test report to backend.
 */
async function sendTestReport(report: TestReport): Promise<void> {
  const backendUrl = getBackendUrl();

  if (!backendUrl) {
    throw new Error("Backend URL not configured");
  }

  const url = `${backendUrl}${TELEMETRY_CONFIG.endpoint}`;

  console.log(`[Telemetry] Sending report to ${url}`);

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(report),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `Failed to send test report: ${response.status} ${errorText}`,
    );
  }

  const result = await response.json();

  console.log("[Telemetry] Backend evaluation result:", {
    passed: result.evaluation?.passed,
    checks: result.evaluation?.checks,
    suggestions: result.evaluation?.suggestions,
  });
}
